import React, { useState, useEffect } from "react";
import {
	fetchAll,
	fetchOne,
	fetchOneWithPopulates,
} from "../utils/fetchResources";
import { Link, useParams, Prompt } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { onReadyCKE, CKEconfig } from "../config/CKE";
import { useSnackbar } from "notistack";
import {
	makeStyles,
	Typography,
	TextField,
	Button,
	Grid,
	Card,
	CardContent,
	Hidden,
	useTheme,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Switch,
	FormGroup,
	FormControlLabel,
	Input,
} from "@material-ui/core";
import SetFile from "../components/SetFile";

const fullName = (person) => {
	let name = "";
	if (person.firstName) {
		name += person.firstName;
	}
	if (person.middleName) {
		name += " " + person.middleName;
	}
	if (person.lastName) {
		name += " " + person.lastName;
	}
	if (person.suffix) {
		name += " " + person.suffix;
	}
	if (person.title) {
		name += ", " + person.title;
	}
	return name;
};

function UnitsEdit(props) {
	const theme = useTheme();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [unit, setUnit] = useState();
	const [site, setSite] = useState();
	const [people, setPeople] = useState([]);
	const [unsaved, setUnsaved] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const [shortDescLength, setShortDescLength] = useState(0);
	const [formattedDate, setFormattedDate] = useState();

	useEffect(() => {
		(async () => {
			await fetchOne("units", id, setUnit);
			await fetchAll("people", setPeople, "lastName");
		})();
	}, [id]);

	useEffect(() => {
		if (unit && unit.shortDescription) {
			setShortDescLength(unit.shortDescription.length);
		}
	}, [unit, setShortDescLength]);

	// Get a list of all the units within the current site from the site's populated data
	useEffect(() => {
		if (unit) {
			(async () => {
				await fetchOneWithPopulates("sites", unit.site, setSite);
				if (unit.sortDate && unit.sortDate.length > 10) {
					setUnit({ ...unit, sortDate: unit.sortDate.substring(0, 10) });
				}
				setLoading(false);
			})();
		}
	}, [unit]);

	const onSubmit = (e) => {
		e.preventDefault();
		fetch(`${process.env.REACT_APP_API_URL}/units/${id}`, {
			method: "PUT",
			credentials: "include",
			body: JSON.stringify(unit),
			headers: { "Content-Type": "application/json" },
		})
			.then((res) => res.json())
			.then((data) => {
				console.log(data);
				if (data.success) {
					setUnsaved(false);
					enqueueSnackbar("Unit updated successfully!", {
						variant: "success",
					});
					fetchOne("units", id, setUnit);
				} else {
					console.log(data);
					const errorArray = data.error.split(",");
					errorArray.forEach((error) => {
						enqueueSnackbar(`${error}`, { variant: "error" });
					});
				}
			});
	};

	const onChange = (e) => {
		setUnit({ ...unit, [e.target.name]: e.target.value });
		setUnsaved(true);
	};

	const onChangeShortDesc = (e) => {
		setShortDescLength(e.target.value.length);
		setUnit({ ...unit, shortDescription: e.target.value });
	};

	const onChangeDescription = (e, editor) => {
		const data = editor.getData();
		setUnit({ ...unit, description: data });
		setUnsaved(true);
	};

	const onChangePublished = (e) => {
		setUnit({ ...unit, [e.target.name]: e.target.checked });
		setUnsaved(true);
	};

	const useStyles = makeStyles((theme) => ({
		paper: {
			marginTop: theme.spacing(8),
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		},
		form: {
			width: "100%", // Fix IE 11 issue.
		},
		submit: {
			margin: theme.spacing(2, 0, 1),
		},
		CKElabels: {
			marginTop: "1.5rem",
			fontWeight: "bold",
		},
		colorSettings: {
			listStyleType: "none",
			marginTop: "1rem",
			padding: 0,
			"& li": {
				margin: ".5rem 0",
			},
		},
		colorLabel: {
			marginLeft: ".5rem",
		},
		info: {
			background: "#cce5ff",
			padding: ".5rem",
			textAlign: "center",
			borderRadius: "5px",
			margin: ".5rem 0",
		},
	}));

	const classes = useStyles();

	if (loading && !site) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Prompt
				when={unsaved}
				message="You have unsaved changes. Are you sure you want to leave?"
			/>
			<form className={classes.form} onSubmit={onSubmit}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={12} md={9}>
						<Typography component="h1" variant="h4">
							Edit Unit:{" "}
							<Link
								style={{ color: theme.palette.primary.main }}
								to={`/${site.slug}/units/${unit.slug}`}
								target="_blank"
							>
								{unit.name}
							</Link>
						</Typography>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="name"
							label="Unit Name"
							name="name"
							size="small"
							required
							onChange={onChange}
							value={unit.name}
						/>
						<Typography className={classes.info}>
							The following date field may be either the unit's start date or
							end date. It is only used for sorting and it will not show up
							anywhere on the public site.
						</Typography>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="sortDate"
							label="Unit Sort Date"
							name="sortDate"
							size="small"
							required
							onChange={onChange}
							type="date"
							InputLabelProps={{
								shrink: true,
							}}
							value={unit.sortDate ?? null}
						/>
						<FormControl
							fullWidth
							size="small"
							margin="normal"
							variant="outlined"
							className={classes.formControl}
						>
							<InputLabel id="parent-unit-label">Parent Unit</InputLabel>
							<Select
								inputProps={{ name: "parent" }}
								value={unit.parent ?? ""}
								onChange={onChange}
								label="Parent Unit"
							>
								<MenuItem value={null}>
									<em>None</em>
								</MenuItem>
								{site.units
									.filter((unit) => !unit.parent)
									.sort((a, b) => (a.name > b.name ? 1 : -1))
									.map((x, index) => {
										//If statement is used to exclude the unit the user is currently editing
										if (unit._id !== x._id) {
											return (
												<MenuItem key={index} value={x._id}>
													{x.name}
												</MenuItem>
											);
										} else {
											return "";
										}
									})}
							</Select>
						</FormControl>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							required
							id="slug"
							label="Slug"
							name="slug"
							size="small"
							onChange={onChange}
							value={unit.slug}
						/>
						<p className={classes.CKElabels} style={{ marginBottom: 0 }}>
							SHORT Description*:
							{shortDescLength !== undefined && shortDescLength <= 500 && (
								<span style={{ color: "green" }}>
									{" " + shortDescLength + " / 500"}
								</span>
							)}
							{shortDescLength !== undefined && shortDescLength > 500 && (
								<span style={{ color: "red" }}>
									{" " + shortDescLength + "/ 500"}
								</span>
							)}
						</p>
						<TextField
							multiline
							minRows={4}
							variant="outlined"
							required
							id="shortDesc"
							label="Short description"
							name="shortDescription"
							onChange={onChangeShortDesc}
							size="small"
							margin="normal"
							fullWidth
							value={unit.shortDescription}
						></TextField>
						<p className={classes.CKElabels}>LONG Description*:</p>
						<CKEditor
							onReady={(editor) => {
								onReadyCKE(editor);
							}}
							editor={Editor}
							onChange={onChangeDescription}
							data={unit.description}
							config={CKEconfig("Unit LONG description...")}
						/>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
						>
							Save
						</Button>
					</Grid>
					<Grid item xs={12} sm={12} md={3}>
						<Card>
							<CardContent>
								<Hidden smDown>
									<Button
										type="submit"
										onClick={onSubmit}
										fullWidth
										variant="contained"
										color="primary"
										className={classes.submit}
									>
										Save
									</Button>
								</Hidden>
								<FormGroup row>
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={unit.published}
												onChange={onChangePublished}
												name="published"
											/>
										}
										label={
											unit.published ? (
												<Typography color="primary">Published</Typography>
											) : (
												<Typography color="secondary">Draft</Typography>
											)
										}
									/>
								</FormGroup>
								<SetFile
									fileTitle="Featured Image"
									resource={unit}
									setResource={setUnit}
									imageOnly={true}
									fileLabel="featuredImage"
									fileLabelFileName="featuredImageFile"
								/>
								<FormControl fullWidth size="medium">
									<InputLabel id="themes-input-label">People</InputLabel>
									<Select
										multiple
										name="people"
										value={unit.people ?? []}
										onChange={onChange}
										input={<Input id="people" />}
										className={classes.peopleSelect}
										renderValue={() => "View Selected"}
									>
										{people &&
											people.map((person) => (
												<MenuItem key={person.id} value={person._id}>
													{fullName(person)}
												</MenuItem>
											))}
									</Select>
								</FormControl>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</form>
		</>
	);
}

export default UnitsEdit;
